import { useMemo, useRef } from "react";
import HowWeWorkCards, {
  type HowWeWorkCardsHandle,
} from "@/pages/home/components/HowWeWorkCards";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

/**
 * HowWeWork — scroll-driven "cards scattering into place" moment.
 *
 * Perf model (v2, unified pattern):
 *  - NO continuous rAF loop, NO JS easing/smoothing.
 *  - Single-flight rAF throttle → one paint per scroll frame.
 *  - Direct 1:1 scroll → visual mapping. Zero drift, perfect sync.
 *  - Diff-cache prevents redundant DOM writes.
 */

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInCubic = (t: number) => t * t * t;

export default function HowWeWork() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsLayerRef = useRef<HowWeWorkCardsHandle>(null);

  const headlineRef = useRef<HTMLHeadingElement>(null);
  const eyebrowRef = useRef<HTMLSpanElement>(null);

  const cardPaintCache = useMemo(
    () =>
      new Array(16).fill(null).map(() => ({
        tx: -9999,
        ty: -9999,
        rot: -9999,
        op: -1,
      })),
    [],
  );
  const lastHeadlineOpacity = useRef(-1);
  const lastEyebrowOpacity = useRef(-1);

  const paint = (p: number) => {
    const headlineT = Math.max(0, Math.min(1, (p - 0.15) / 0.4));
    const headlineOpacity = 1 - headlineT * 0.45;
    const eyebrowOpacity = Math.max(0, Math.min(1, p / 0.25));

    if (
      headlineRef.current &&
      Math.abs(headlineOpacity - lastHeadlineOpacity.current) > 0.005
    ) {
      headlineRef.current.style.opacity = headlineOpacity.toFixed(3);
      lastHeadlineOpacity.current = headlineOpacity;
    }
    if (
      eyebrowRef.current &&
      Math.abs(eyebrowOpacity - lastEyebrowOpacity.current) > 0.005
    ) {
      eyebrowRef.current.style.opacity = eyebrowOpacity.toFixed(3);
      lastEyebrowOpacity.current = eyebrowOpacity;
    }

    const handle = cardsLayerRef.current;
    if (handle) {
      const config = handle.getConfig();
      const els = handle.getCards();
      for (let i = 0; i < config.length; i++) {
        const c = config[i];
        const el = els[i];
        if (!el) continue;

        const enterRawT = (p - c.enterStart) / (c.enterEnd - c.enterStart);
        const enterT = Math.max(0, Math.min(1, enterRawT));
        const enterEased = easeOutCubic(enterT);

        let tx = c.fromX * (1 - enterEased);
        let ty = c.fromY * (1 - enterEased);
        let rot = c.fromRot + (c.toRot - c.fromRot) * enterEased;
        let opacity = Math.max(0, Math.min(1, enterRawT * 1.5));

        if (c.exitStart !== undefined && c.exitEnd !== undefined) {
          const exitRawT = (p - c.exitStart) / (c.exitEnd - c.exitStart);
          const exitT = Math.max(0, Math.min(1, exitRawT));
          const exitEased = easeInCubic(exitT);
          const dy = c.exitDy ?? -50;
          ty += dy * exitEased;
          rot += (c.toRot > 0 ? -3 : 3) * exitEased;
          opacity *= 1 - exitEased;
        }

        const cache = cardPaintCache[i];
        const txChanged = Math.abs(tx - cache.tx) > 0.05;
        const tyChanged = Math.abs(ty - cache.ty) > 0.05;
        const rotChanged = Math.abs(rot - cache.rot) > 0.05;
        if (txChanged || tyChanged || rotChanged) {
          el.style.transform =
            `translate3d(${tx.toFixed(2)}vw, ${ty.toFixed(2)}vh, 0) ` +
            `rotate(${rot.toFixed(2)}deg)`;
          cache.tx = tx;
          cache.ty = ty;
          cache.rot = rot;
        }
        if (Math.abs(opacity - cache.op) > 0.005) {
          el.style.opacity = opacity.toFixed(3);
          cache.op = opacity;
        }
      }
    }
  };

  useSectionScrollProgress(sectionRef, (p, inView) => {
    if (!inView) return;
    paint(p);
  });

  return (
    <section
      ref={sectionRef}
      id="how-we-work"
      className="relative w-full bg-background-50"
      style={{
        height: "360vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      <div
        className="sticky top-0 w-full h-screen overflow-hidden"
        style={{ contain: "layout paint" }}
      >
        {/* Section eyebrow / meta */}
        <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
          <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
            <span
              ref={eyebrowRef}
              className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium"
              style={{ opacity: 0 }}
            >
              Our Methods
            </span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
              Chapter 04 · How we work
            </span>
          </div>
        </div>

        {/* Big background headline */}
        <div className="absolute inset-0 z-10 flex items-center justify-center px-6 pointer-events-none">
          <h2
            ref={headlineRef}
            className="text-center leading-[0.9] whitespace-nowrap select-none"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(5rem, 14vw, 15rem)",
              fontWeight: 400,
              letterSpacing: "-0.035em",
              fontVariationSettings: "'opsz' 144",
              color: "oklch(var(--foreground-950))",
              opacity: 1,
              // Promote to its own compositor layer so the opacity animation
              // stays on the compositor and doesn't force a full-viewport
              // repaint of the giant 15rem/240px-wide text every scroll frame.
              willChange: "opacity, transform",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
            }}
          >
            How We Work
          </h2>
        </div>

        {/* Cards layer */}
        <HowWeWorkCards ref={cardsLayerRef} />

        {/* Corner meta */}
        <div className="absolute bottom-8 left-6 md:left-10 z-50 flex items-center gap-3 text-foreground-950/55">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Chapter 04
          </span>
          <span className="w-8 h-[1px] bg-foreground-950/40"></span>
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Methods
          </span>
        </div>

        <div className="absolute bottom-8 right-6 md:right-10 z-50 flex items-center gap-3 text-foreground-950/55">
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Scroll to gather
          </span>
          <span className="w-8 h-[1px] bg-foreground-950/40"></span>
        </div>
      </div>
    </section>
  );
}